<!-- ContractList.vue -->
<template>
  <v-container>
    <v-row v-if="contracts.length">
        <h2 class="text-h4 mb-4 font-weight-medium grey--text text--darken-1">
          <v-icon left color="grey darken-1">mdi-file-document-multiple</v-icon>
          Contratos Cadastrados
        </h2>
    </v-row>

    <v-row v-if="contracts.length === 0" class="justify-center align-center fill-height" style="min-height: 50vh; display: flex;">
      <v-col cols="12" class="text-center">
        <v-icon
            size="large"
            color="grey lighten-1"
            class="mb-4"
        >
          mdi-file-document-outline
        </v-icon>
        <h3 class="text-h6 grey--text">
          Nenhum contrato encontrado
        </h3>
        <p class="text-subtitle-1 grey--text text--lighten-1">
          Você ainda não possui contratos cadastrados.
        </p>
      </v-col>
    </v-row>

    <!-- Lista de contratos -->
    <v-row v-else>
      <v-col
          v-for="contract in contracts"
          :key="contract.id"
          cols="12"
          md="6"
      >
        <v-card
            hover
            elevation="3"
            class="contract-card"
            @click="editContract(contract)"
        >
          <v-card-title class="d-flex justify-space-between align-center">
            <span :title="templateNames[contract.selectedTemplateId] || getProposalTypeLabel(contract.selectedProposalId)" class="text-subtitle-1 font-weight-bold text-truncate">
              <v-icon small color="primary" class="mr-2">mdi-file-document</v-icon>
              {{ templateNames[contract.selectedTemplateId] || 'Carregando...' }}
            </span>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    v-bind="attrs"
                    @click.stop="downloadContract(contract)"
                    v-on="on"
                    color="primary">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
            </v-menu>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <div class="d-flex flex-column">
              <div class="d-flex justify-space-between mb-2 align-center">
                <span class="grey--text text--darken-1">
                  <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                  Criado em:
                </span>
                <span class="font-weight-medium">
                  {{ formatDate(contract.createdAt) }}
                </span>
              </div>
              <div class="d-flex justify-space-between align-center">
                <span class="grey--text text--darken-1">
                  <v-icon x-small class="mr-1">mdi-update</v-icon>
                  Atualizado em:
                </span>
                <span class="font-weight-medium">
                  {{ formatDate(contract.updatedAt) }}
                </span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="isLoading" max-width="320" persistent>
      <v-list class="py-5" color="primary" elevation="12" rounded>
        <v-list-item class="d-flex justify-space-between">
          <div>
            <span class="white--text text-subtitle-1">Baixando Contrato...</span>
          </div>
          <div>
            <v-progress-circular color="#1A237E" indeterminate size="32" width="2"></v-progress-circular>
          </div>
        </v-list-item>
      </v-list>
    </v-dialog>

    <v-dialog v-model="dialogDownload" max-width="400">
      <v-card class="download-dialog">
       <v-card-title class="headline d-flex align-center">
         <v-icon color="primary" class="mr-2">mdi-download</v-icon>
         Salvar Arquivo
       </v-card-title>
       <v-card-text class="pt-4">
         <p class="text-body-1">
           O arquivo está pronto para ser baixado. Clique no botão abaixo para salvá-lo em seu computador.
         </p>
         <v-alert
           type="info"
           variant="tonal"
           class="mt-3"
           icon="mdi-information"
         >
           Após o download, esta janela fechará automaticamente.
         </v-alert>
       </v-card-text>
       <v-card-actions class="pa-4">
         <v-spacer></v-spacer>
         <a :href="fileLink" download :download="fileToShow ? fileToShow.name : 'contrato'" class="text-decoration-none">
           <v-btn
             color="primary"
             variant="elevated"
             prepend-icon="mdi-download"
             @click="dialogDownload = false"
           >
             Salvar Arquivo
           </v-btn>
         </a>
         <v-btn
           color="white"
           variant="text"
           class="ml-2"
           @click="dialogDownload = false"
         >
           Fechar
         </v-btn>
       </v-card-actions>
     </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import {showErrorSnackBar} from "@/utils/SnackBartHelper";
import ContractRequestStorage from "@/services/storage/ContractRequestStorage";
import ContractsTemplatesService from "@/services/server/ContractsTemplatesService";
import DiagnosticProposal from "@/services/firebase/DiagnosticProposal/DiagnosticProposalService";

export default {
  name: 'ContractList',
  props: {
    contracts: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    isLoading: false,
    fileLink: null,
    fileToShow: null,
    dialogDownload: false,
    templateNames: {}, // Map of template IDs to template names
  }),
  async created() {
    // Fetch template names for all contracts when component is created
    await this.fetchAllTemplateNames();
  },
  watch: {
    contracts: {
      handler: 'fetchAllTemplateNames',
      deep: true
    }
  },
  methods: {
    async fetchAllTemplateNames() {
      if (!this.contracts || !this.contracts.length) return;
      
      // Get unique template IDs from all contracts
      const templateIds = [...new Set(this.contracts.map(contract => contract.selectedTemplateId))];
      
      // Fetch template names for each ID
      for (const templateId of templateIds) {
        if (templateId) {
          await this.getTemplateName(templateId);
        }
      }
    },
    truncateTitle(text, maxLength = 35) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
      }
      return text;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    async getTemplateName(templateId) {
      if (!templateId) return 'Template não definido';
      
      // If we already fetched this template name, return it
      if (this.templateNames[templateId]) {
        return this.templateNames[templateId];
      }
      
      try {
        const templateData = await ContractsTemplatesService.getTemplateById(templateId);
        const templateName = templateData.data.data.templateName;
        
        // Cache the template name for future use
        this.$set(this.templateNames, templateId, templateName);
        
        return templateName;
      } catch (error) {
        console.error('Erro ao buscar template:', error);
        return 'Nome do template indisponível';
      }
    },
    async downloadContract(contract) {
        if (contract.currentAttachedContract.path) {
          try {
            this.isLoading = true

            const fileBlob = await ContractRequestStorage.downloadImg(contract.currentAttachedContract.path);
            this.fileToShow = new File([fileBlob], contract.currentAttachedContract.name, {
              type: fileBlob.type
            });
            this.fileLink = URL.createObjectURL(this.fileToShow);
            this.dialogDownload = true;

            this.isLoading = false
          } catch (e) {
            console.error("error", e)
            this.isLoading = false
          } finally {
            this.isLoading = false
          }
        } else {
        showErrorSnackBar("Contrato não disponível")
      }
    },
    editContract(contract) {
      this.$emit('edit-contract', contract);
    },
    async getProposalTypeLabel(idProposal) {
     
      const types = {
        'model_performance': 'Modelo Performance',
        'investment': 'Venda do Projeto',
        'pre_fixed': 'Pré-Fixado',
        'management': 'Gestão'
      };

      const proposalType = await DiagnosticProposal.getProposalById(idProposal);
      
      return types[proposalType] || proposalType;
    },
  }
}
</script>

<style scoped>
.contract-card {
  transition: all 0.2s ease;
  border-left: 3px solid var(--v-primary-base, #1976d2);
  border-radius: 4px;
}

.contract-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
</style>
